import React, { useEffect, useState, useCallback } from 'react';
import { Users, Plus, Edit2, Trash2, X, Power } from 'lucide-react';
import { toast } from 'sonner';
import { api, inputCls } from './targets/api';

const EMPTY = { name: '', email: '', phone: '', designation: '', company_id: '', bu_ids: [], role: 'salesperson', user_id: '', hr_emp_id: '', active: true };

const PersonModal = ({ initial, companies, bus, logins, hrEmployees, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleBu = (id) => setForm({ ...form, bu_ids: form.bu_ids.includes(id) ? form.bu_ids.filter(b => b !== id) : [...form.bu_ids, id] });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api(initial.id ? `/team/${initial.id}` : '/team', { method: initial.id ? 'PUT' : 'POST', body: JSON.stringify({ ...form, company_id: form.company_id || null, user_id: form.user_id || null, hr_emp_id: form.hr_emp_id || null }) });
      toast.success(initial.id ? 'Salesperson updated' : 'Salesperson added');
      onSaved();
    } catch (err) { toast.error(err.message); }
  };

  const visibleBus = bus.filter(b => !form.company_id || b.company_id === form.company_id);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <form onSubmit={save} className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sales-person-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">{initial.id ? 'Edit Salesperson' : 'New Salesperson'}</h2>
          <button type="button" onClick={onClose} data-testid="sales-person-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-600">Name *</label><input required value={form.name} onChange={set('name')} className={inputCls} data-testid="sp-name-input" /></div>
          <div><label className="text-xs font-medium text-slate-600">Designation</label><input value={form.designation || ''} onChange={set('designation')} className={inputCls} data-testid="sp-designation-input" /></div>
          <div><label className="text-xs font-medium text-slate-600">Phone</label><input value={form.phone || ''} onChange={set('phone')} className={inputCls} data-testid="sp-phone-input" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-600">Email</label><input type="email" value={form.email || ''} onChange={set('email')} className={inputCls} data-testid="sp-email-input" /></div>
          <div>
            <label className="text-xs font-medium text-slate-600">Role</label>
            <select value={form.role || 'salesperson'} onChange={set('role')} className={inputCls} data-testid="sp-role-select">
              <option value="salesperson">Salesperson</option>
              <option value="team_lead">Team Lead</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-600">HR employee record</label>
            <select value={form.hr_emp_id || ''} onChange={e => {
              const emp = hrEmployees.find(h => h.emp_id === e.target.value);
              setForm({ ...form, hr_emp_id: e.target.value, name: form.name || emp?.name || '', email: form.email || emp?.email || '', phone: form.phone || emp?.phone || '', designation: form.designation || emp?.designation || '' });
            }} className={inputCls} data-testid="sp-hr-select">
              <option value="">Not linked</option>
              {hrEmployees.map(h => <option key={h.emp_id} value={h.emp_id}>{h.name} ({h.emp_id}{h.department ? ` • ${h.department}` : ''})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">App login</label>
            <select value={form.user_id || ''} onChange={set('user_id')} className={inputCls} data-testid="sp-login-select">
              <option value="">Not linked</option>
              {logins.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-600">Company</label>
            <select value={form.company_id || ''} onChange={set('company_id')} className={inputCls} data-testid="sp-company-select">
              <option value="">All / not set</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-600">Business Units handled</label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="sp-bu-chips">
              {visibleBus.length === 0 && <span className="text-xs text-slate-400">No business units yet — add them in Targets &amp; Roadmap → Companies &amp; BUs</span>}
              {visibleBus.map(b => (
                <button type="button" key={b.id} onClick={() => toggleBu(b.id)} data-testid={`sp-bu-${b.id}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${form.bu_ids.includes(b.id) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                  {b.code || b.name}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="sp-active-checkbox" /> Active</label>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" data-testid="sp-save-btn" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800">Save</button>
        </div>
      </form>
    </div>
  );
};

export default function SalesTeam() {
  const [team, setTeam] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bus, setBus] = useState([]);
  const [editing, setEditing] = useState(null);
  const [logins, setLogins] = useState([]);
  const [hrEmployees, setHrEmployees] = useState([]);

  const load = useCallback(async () => {
    try {
      const [t, c, b] = await Promise.all([api('/team'), api('/companies'), api('/business-units')]);
      setTeam(t); setCompanies(c); setBus(b);
      api('/logins').then(setLogins).catch(() => setLogins([]));
      api('/hr-employees').then(setHrEmployees).catch(() => setHrEmployees([]));
    } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buName = (id) => bus.find(b => b.id === id)?.code || bus.find(b => b.id === id)?.name || '';
  const companyName = (id) => companies.find(c => c.id === id)?.name || '-';

  const toggle = async (p) => {
    try { await api(`/team/${p.id}`, { method: 'PUT', body: JSON.stringify({ ...p, active: !p.active }) }); load(); } catch (err) { toast.error(err.message); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Their salesperson targets are removed too. Past enquiries keep the name.`)) return;
    try { await api(`/team/${p.id}`, { method: 'DELETE' }); toast.success('Deleted'); load(); } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="sales-team-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" /> Sales Team</h1>
          <p className="text-slate-500 mt-1 text-sm">Salespeople used for enquiries, targets and performance analysis</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="sp-add-btn" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"><Plus className="w-4 h-4" /> New Salesperson</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{['Name', 'Role', 'Login', 'Contact', 'Company', 'Business Units', 'Status', ''].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {team.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500" data-testid="sp-empty">No salespeople yet</td></tr>
            ) : team.map(p => (
              <tr key={p.id} className={`hover:bg-slate-50 ${p.active ? '' : 'opacity-60'}`} data-testid={`sp-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3 text-slate-600"><p>{p.role === 'team_lead' ? 'Team Lead' : 'Salesperson'}</p><p className="text-xs text-slate-400">{p.designation}</p></td>
                <td className="px-4 py-3 text-xs" data-testid={`sp-login-${p.id}`}>{p.user_id ? <span className="text-emerald-700">{logins.find(u => u.id === p.user_id)?.email || 'Linked'}</span> : <span className="text-slate-400">Not linked</span>}</td>
                <td className="px-4 py-3 text-slate-600"><p>{p.phone || '-'}</p><p className="text-xs text-slate-400">{p.email}</p></td>
                <td className="px-4 py-3 text-slate-600">{companyName(p.company_id)}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(p.bu_ids || []).map(id => <span key={id} className="px-2 py-0.5 text-xs bg-slate-100 rounded-full">{buName(id)}</span>)}</div></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs rounded-full ${p.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{p.active ? 'Active' : 'Disabled'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => toggle(p)} title={p.active ? 'Disable' : 'Enable'} data-testid={`sp-toggle-btn-${p.id}`} className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded"><Power className="w-4 h-4" /></button>
                    <button onClick={() => setEditing(p)} title="Edit" data-testid={`sp-edit-btn-${p.id}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} title="Delete" data-testid={`sp-delete-btn-${p.id}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PersonModal initial={editing} companies={companies} bus={bus} logins={logins} hrEmployees={hrEmployees} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
